import React from "react";
import dayjs from "dayjs";
import { TitleText } from "../../../shared/styled/TitleBar";
import Status from "../../../shared/components/Status";
import LoadingButton from "../../../shared/components/LoadingButton";
import UserCheckIcon from "../../../shared/svg/UserCheckIcon";
import Calendar from "../../../shared/svg/Calendar";
import { OutsourceOrder } from "../../../data/entities";
import { OutsourceOrderFilterType, valueToText } from "../../../configs/filterTypes";
import { getStatusColorTheme } from "../../OutsourceOrderList/Content/OutsourceOrderTable";
import { StyledButton } from "../Styled";
import {
    HeaderLeft,
    HeaderActions,
    HeaderWrapper,
    IconWrap,
    MetaBar,
    MetaDividerV,
    MetaItem,
    MetaLabel,
    MetaRow,
    MetaValue,
    TitleRow,
    UrgentDot,
    UrgentText,
} from "./Styled";
import { OutsourceOrderActionState } from "./actions";

interface OutsourceOrderHeaderProps {
    outsourceOrder: OutsourceOrder;
    actionState: OutsourceOrderActionState;
    isLoading: boolean;
    isUpdating: boolean;
    onSaveEdit: () => void;
    onCancelOrder: () => void;
    onSendToStorage: () => void;
    onOpenExport: () => void;
    onOpenReceipt: () => void;
    onComplete: () => void;
    onDownloadExport: () => void;
    onDownloadOrder: () => void;
}

export const OutsourceOrderHeader = ({
    outsourceOrder,
    actionState,
    isLoading,
    isUpdating,
    onSaveEdit,
    onCancelOrder,
    onSendToStorage,
    onOpenExport,
    onOpenReceipt,
    onComplete,
    onDownloadExport,
    onDownloadOrder,
}: OutsourceOrderHeaderProps) => (
    <HeaderWrapper>
        <HeaderLeft>
            <TitleRow>
                <TitleText>
                    {outsourceOrder.name ? outsourceOrder.name : outsourceOrder.id}
                </TitleText>
                <Status
                    colorTheme={getStatusColorTheme(outsourceOrder.status)}
                    text={valueToText(OutsourceOrderFilterType, "status", outsourceOrder.status)}
                    width="156px"
                />
            </TitleRow>

            <MetaRow>
                <MetaBar>
                    {outsourceOrder?.is_urgent && (
                        <>
                            <UrgentDot />
                            <UrgentText>Khẩn cấp</UrgentText>
                            <MetaDividerV />
                        </>
                    )}
                    {outsourceOrder.due_date && (
                        <MetaItem>
                            <IconWrap>
                                <Calendar />
                            </IconWrap>
                            <MetaLabel>Hạn</MetaLabel>
                            <MetaValue>
                                {dayjs(outsourceOrder.due_date).format("DD/MM/YYYY")}
                            </MetaValue>
                        </MetaItem>
                    )}
                </MetaBar>
            </MetaRow>
        </HeaderLeft>

        <HeaderActions>
            {actionState.canCancel && (
                <StyledButton variant="text" onClick={onCancelOrder}>
                    Huỷ đơn hàng
                </StyledButton>
            )}

            {actionState.canEdit && (
                <LoadingButton
                    variant="outlined"
                    onClick={onSaveEdit}
                    disabled={isLoading || isUpdating}
                    text="Cập nhật đơn hàng"
                    isLoading={isUpdating}
                />
            )}

            {actionState.canSendToStorage && (
                <LoadingButton
                    tooltipText="Cần thêm ít nhất một vật tư gia công"
                    text="Gửi tới thủ kho"
                    disabled={actionState.isSendToStorageDisabled}
                    isLoading={isUpdating}
                    onClick={onSendToStorage}
                    icon={<UserCheckIcon />}
                />
            )}

            {actionState.canExport && (
                <StyledButton
                    variant="contained"
                    onClick={onOpenExport}
                    disabled={actionState.isExportDisabled}
                >
                    Xuất kho
                </StyledButton>
            )}

            {actionState.canReceive && (
                <StyledButton variant="contained" onClick={onOpenReceipt}>
                    Nhập về
                </StyledButton>
            )}

            {actionState.canComplete && (
                <LoadingButton
                    variant="contained"
                    onClick={onComplete}
                    disabled={isLoading || isUpdating}
                    isLoading={isLoading || isUpdating}
                    text="Hoàn thành đơn hàng"
                />
            )}

            {actionState.canDownloadOrder && (
                <StyledButton variant="contained" onClick={onDownloadOrder} disabled={isLoading}>
                    Tải đơn hàng gia công
                </StyledButton>
            )}

            {actionState.canDownloadExport && (
                <StyledButton variant="contained" onClick={onDownloadExport}>
                    Tải phiếu
                </StyledButton>
            )}
        </HeaderActions>
    </HeaderWrapper>
);
